/**
 * The mock backend's domain logic: state, GraphQL query/mutation handlers, login.
 *
 * The mock is NOT a second backend. Its goal is exactly (docs/liquido-testing.md, section 6.6):
 *  1. the two happy cases run against it: happy-case.cy.js and polly-happy-case.cy.js,
 *  2. just enough data that src/views/_design-overview.vue can show every screen,
 *  3. its seed is src/mockdata/teamsSeed.json, as exported from a seeded backend.
 * Whether a new test is covered by extending the mock, or skips in mock mode, is Robert's decision.
 *
 * Deliberately free of Node-only APIs (no crypto, no Buffer, no raw HTTP req/res) so it can be imported from BOTH:
 *
 *  - liquido-graphql-client.js, browser-bundled code, for config.test.js (vitest, configSource
 *    "test") - there is no Vite dev server in a vitest run to route an HTTP call through, so
 *    graphQlQuery() calls graphQlQueryMock() here directly, in-process.
 *  - mock-backend/liquido-mock-http-server.js, which answers the SAME logic over real HTTP inside
 *    the Vite dev server's Node process, for config.development.js (Cypress e2e runs). That is what
 *    lets cy.intercept()/cy.wait() see mocked traffic exactly like they would the real backend, so
 *    the same e2e spec source works unmodified against either backend - see that file's module doc
 *    comment for the rest of the story (REST endpoints, WebAuthn) and why it has to be a separate,
 *    Node-only file.
 *
 * Ported from the old src/services/liquido-graphql-client.mock.js, which did the same job by
 * intercepting axios requests client-side. Most of the actual mock domain logic below (state,
 * queryHandlers, mutationHandlers) is unchanged - only what calls into it changed.
 */
import { get, isValidString, set } from "@kubric/litedash"
import config from "config"
// The mock's one and only seed: the backend's fixed test teams (loginTeam, multiTeamA/B, scratchTeam)
// and the newest seed team "Test Team <millis>", exported from a seeded database by
// scripts/export-mock-seed.mjs - the same data a real backend has after TestDataCreator ran.
import teamsSeedMock from "./teamsSeed.json"
import LiquidoExceptionCodes from "@/liquido/services/LiquidoExceptionCodes.js"
import { decodeJwtPayload, LIQUIDO_ADMIN_ROLE, LIQUIDO_USER_ROLE } from "@/liquido/services/jwt-util.js"

const deepClone = val => JSON.parse(JSON.stringify(val))
const nowIso = () => new Date().toISOString()

/** Exported so liquido-mock-http-server.js's writeGraphQlResponse() can recognize it with instanceof. */
export class MockLiquidoError extends Error {
	constructor(code, message) {
		super(message)
		this.code = code
	}
}

/**
 * The `liquidoException` object exactly as the real backend sends it - in a GraphQL error's
 * extensions (LiquidoErrorExtensionProvider) and in a REST error body (LiquidoExceptionMapper) alike.
 * Same three fields in both places, so the mock cannot drift into a shape the backend never sends.
 */
export const mockLiquidoException = (code, message) => ({
	liquidoErrorName: Object.keys(LiquidoExceptionCodes).find(name => LiquidoExceptionCodes[name] === code),
	liquidoErrorCode: code,
	liquidoErrorMessage: message,
})

const DAY_MILLIS = 24 * 60 * 60 * 1000

/**
 * Creates the initial mock state: the teams from teamsSeed.json, plus the id counters for new polls
 * and proposals, which start above every id in the seed.
 */
const createState = () => {
	const teams = deepClone(teamsSeedMock.teams || [])
	const polls = teams.flatMap(t => t.polls || [])

	// The export's dates are frozen at the moment it was made. Move the polls in voting into the
	// present: started yesterday, and ending 300 days ahead like the backend's seed polls
	// (TestFixtures.seedVotingEnd()), so that closeOverduePolls() - the mock's PollCloser - never
	// finishes them while a dev server runs.
	polls.filter(poll => poll.status === "VOTING").forEach(poll => {
		poll.votingStartAt = new Date(Date.now() - DAY_MILLIS).toISOString()
		poll.votingEndAt = new Date(Date.now() + 300 * DAY_MILLIS).toISOString()
	})

	return {
		// A user can be a member of SEVERAL teams, so the mock holds a list and remembers which one the
		// current request is in - see setAuthHeader(). Reach for it through currentTeam().
		teams,
		currentTeamIndex: 0,
		issuedAuthTokensByMobile: {},
		// One-time tokens of the "verify your email" links, keyed by the user's (lower-cased) email - see
		// liquido-mock-http-server.js. A new link replaces the user's previous one, like the backend.
		emailVerificationTokensByEmail: {},
		// The mock sends no mail. It only remembers the link each mail would have carried, as
		// { to, link }, and GET /mock/mailLinks hands them to the e2e specs (tests/e2e/support/mailbox.js).
		mailLinks: [],
		voterTokensByPollAndUser: {},
		ballotsByPollAndUser: {},
		nextPollId: Math.max(0, ...polls.map(p => p.id)) + 1,
		nextProposalId: Math.max(0, ...polls.flatMap(p => (p.proposals || []).map(pr => pr.id))) + 1,
		// Uploaded profile pictures: picKey -> the picture, base64 (no Buffer in this file), served by
		// GET /picture/:picKey - see liquido-mock-http-server.js. Unlike the backend the mock does not re-encode them.
		picturesByKey: {},
	}
}

/**
 * The mock "database". Module-scoped, so it simply lives for as long as the importing process
 * does - a vitest run, or the Vite dev server - no sessionStorage/localStorage needed now that this
 * never runs in the browser. That is a closer match to a real backend anyway (one shared database,
 * not one per browser tab): restart the dev server (or POST /mock/reset, the red LIQUIDO icon) for a
 * fresh one, same as you would a real database.
 *
 * Exported as a live binding: liquido-mock-http-server.js reads/writes it directly (the mail link
 * tokens and mailLinks in particular) rather than going through this file's handlers for everything.
 */
export let mockState = createState()

/**
 * The Authorization header of the request currently being handled, so the pure handler functions
 * below (queryHandlers.loginWithJwt) and findMemberByCurrentAuthHeader() (used by
 * liquido-mock-http-server.js's WebAuthn handlers) can read "who is calling" without threading it
 * through every function signature - see setAuthHeader() below.
 */
let currentAuthHeader

/**
 * Set before handling a request - see graphQlQueryMock() and liquido-mock-http-server.js.
 *
 * Like the backend, the caller IS whoever the request's JWT names: that user, in that team. The mock
 * used to know just one "current user" - whoever logged in last - which mixed people up as soon as
 * two of them were active at once, e.g. in the design overview's iframes. A request without a JWT,
 * or with one for nobody the mock knows, leaves the session as it is: anonymous endpoints do not
 * look at it, and the login endpoints set it themselves through loginMock().
 */
export const setAuthHeader = value => {
	currentAuthHeader = value
	const payload = decodeJwtPayload(jwtFromAuthHeader())
	const team = payload && mockState.teams.find(t => String(t.id) === String(payload.teamId))
	const member = team && findMemberIn(team, m => cleanEmail(m.user?.email) === cleanEmail(payload.sub))
	if (member) {
		mockState.currentTeamIndex = mockState.teams.indexOf(team)
		mockState.currentUser = deepClone(member.user)
	}
}

const asInt = value => parseInt(value, 10)
const ballotKey = (pollId, userId) => `${pollId}:${userId}`
const stripQuotes = value => (value || "").replace(/^"|"$/g, "")

const argFromQuery = (query, key, defaultValue = undefined) => {
	const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
	const regex = new RegExp(`${escapedKey}\\s*:\\s*([^,)]+)`, "i")
	const match = query.match(regex)
	if (!match) return defaultValue
	return stripQuotes(match[1].trim())
}

const voteOrderFromQuery = query => {
	const match = query.match(/voteOrderIds\s*:\s*\[([^\]]*)\]/i)
	if (!match) return []
	return match[1]
		.split(",")
		.map(it => it.trim())
		.filter(Boolean)
		.map(it => parseInt(it, 10))
		.filter(Number.isFinite)
}

const rejectLiquido = (code, message) => {
	throw new MockLiquidoError(code, message)
}

/**
 * Normalize an email address the way the backend does (DoogiesUtil.cleanEmail): trimmed and lower
 * case. The backend stores every address like that and compares them like that, so the mock does too.
 */
export const cleanEmail = email => (email || "").trim().toLowerCase()

/**
 * Stand-in for the backend's GET /graphql/schema.graphql. The real one serves the full schema as
 * SDL text, not JSON; callers only use it to see that the GraphQL API answers at all, so a tiny
 * excerpt is enough.
 */
export const MOCK_GRAPHQL_SCHEMA = `type Query {
  ping: String
}

type Mutation {
  castVote(pollId: BigInteger!, voteOrderIds: [BigInteger]!, voterToken: String!): CastVoteResult
}
`

const detectOperation = query => {
	// ORDER MATTERS: the first name found anywhere in the query string wins, and the query string
	// includes the whole result selection. So an operation whose result mentions "team" or "polls"
	// - which every login-shaped one does - must be listed BEFORE those generic names, or it gets
	// misrouted to them. That is why switchTeam sits at the very front.
	const operations = [
		"switchTeam", "uploadMyPicture", "removeMyPicture",
		"createNewTeam", "joinTeam", "createPoll", "updatePoll", "addProposal", "updateProposal", "deleteProposal", "likeProposal", "startVotingPhase",
		"finishVotingPhase", "castVote", "loginWithEmailPassword", "googleOneTapLogin", "loginWithAuthToken",
		"teamForInviteCode", "newestSeedTeam", "loginWithJwt",
		"devLogin", "authToken", "voterToken", "verifyBallot", "myBallot", "publishedTally", "polls", "poll", "team", "liquidoConfig", "ping",
	]
	for (const name of operations) {
		if (new RegExp(`\\b${name}\\s*\\(`).test(query) || new RegExp(`\\b${name}\\b`).test(query)) {
			return name
		}
	}
	return null
}

/** The team the mock session is currently scoped to. This replaced the old single mockState.team. */
export const currentTeam = () => mockState.teams[mockState.currentTeamIndex]

/** Every team the given email is a member (or admin) of - the mock's TeamMemberEntity.findTeamsByMember. */
const teamsOfMember = email =>
	(mockState.teams || []).filter(t => (t.members || []).some(m => m.user?.email === email))

/**
 * Members are looked up across ALL teams, not just the current one: a user who belongs only to the
 * second team must still be able to log in. Prefer a hit in the current team so that "who am I"
 * stays stable while a session is scoped somewhere.
 */
const findMemberIn = (team, predicate) => (team?.members || []).find(predicate)
const findMemberAnywhere = predicate =>
	findMemberIn(currentTeam(), predicate) ||
	(mockState.teams || []).flatMap(t => t.members || []).find(predicate)

export const findMemberByEmail = email => findMemberAnywhere(m => cleanEmail(m.user?.email) === cleanEmail(email))
const findMemberByMobile = mobile => findMemberAnywhere(m => m.user?.mobilephone === mobile)
const findPoll = pollId => (currentTeam().polls || []).find(p => p.id === pollId)

const jwtFromAuthHeader = () => {
	const match = (currentAuthHeader || "").match(/^Bearer\s+(.+)$/i)
	return match ? match[1] : undefined
}

/**
 * Mint a structurally real JWT: header.payload.signature, base64url encoded, carrying the same
 * "groups" claim the backend mints in JwtTokenUtils.generateToken.
 *
 * Not signed - the signature segment is a constant, and nothing in the mock ever verifies it. But it
 * must have the right SHAPE, because api.isAdmin() reads the admin role out of this claim (see
 * jwt-util.js). The old plain `mock-jwt-<id>` string had no payload at all, so with a JWT-based
 * isAdmin() the mock backend would have had no admins anywhere.
 *
 * @returns {String} a JWT whose "sub" is the user's email and "groups" reflects their role in `team`
 */
const mintMockJwt = (user, team) => {
	const isAdmin = (team?.members || []).some(m => m.role === "ADMIN" && String(m.user?.id) === String(user.id))
	const groups = isAdmin ? [LIQUIDO_ADMIN_ROLE, LIQUIDO_USER_ROLE] : [LIQUIDO_USER_ROLE]
	// UTF-8 before base64: a JWT segment encodes BYTES. Handing btoa() a string with non-ASCII in it
	// encodes Latin-1, which decodeJwtPayload would then read back as mojibake - and btoa throws
	// outright above U+00FF, which would take the whole mock login down for one unusual address.
	const b64url = obj => btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(obj))))
		.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
	return b64url({ alg: "none", typ: "JWT" }) + "." +
		b64url({ sub: user.email, teamId: String(team?.id), groups }) + ".mocksignature"
}

/** Every mock JWT carries "sub" as the user's email - decode and look them up by it. */
const findMemberByJwt = jwt => {
	const email = decodeJwtPayload(jwt)?.sub
	return email ? findMemberByEmail(email) : undefined
}

/** The member the CURRENT request's Authorization header belongs to - see setAuthHeader(). */
export const findMemberByCurrentAuthHeader = () => findMemberByJwt(jwtFromAuthHeader())

const currentUserOrThrow = () => {
	if (!mockState.currentUser) {
		rejectLiquido(LiquidoExceptionCodes.UNAUTHORIZED, "Mock user is not authenticated")
	}
	return mockState.currentUser
}

/**
 * Central login method for mock backend.
 * Simulates the complete login logic that happens in the real graphQlApi.login() method.
 * This sets up the mock state properly so that subsequent API calls work correctly.
 *
 * Mirrors the real JwtTokenUtils.doLoginInternal(): the session is scoped to ONE team, which is the
 * requested one if given, otherwise the team the session is already in when the user belongs to it
 * (the mock's stand-in for lastTeamId), otherwise their first team.
 *
 * @param {String} email email of the user to log in
 * @param {Number} teamId (optional) pin the session to this team of the user
 * @returns {Object} login result { team, user, jwt, teams } ready to be passed to real graphQlApi.login()
 * @throws MockLiquidoError if email is not found, or is not a member of teamId
 */
export const loginMock = (email, teamId) => {
	const member = findMemberByEmail(email)
	if (!member) {
		rejectLiquido(LiquidoExceptionCodes.UNAUTHORIZED, "Cannot mockLogin: user email not found: " + email)
	}

	const user = member.user
	const usersTeams = teamsOfMember(user.email)
	if (usersTeams.length === 0) {
		rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_USER_NOT_MEMBER_OF_TEAM, "Mock user is not member of any team")
	}

	let team
	if (teamId != null) {
		team = usersTeams.find(t => t.id === teamId)
		if (!team) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_USER_NOT_MEMBER_OF_TEAM,
				`Mock user <${user.email}> is not a member of team ${teamId}`)
		}
	} else {
		team = usersTeams.find(t => t.id === currentTeam().id) || usersTeams[0]
	}
	mockState.currentTeamIndex = mockState.teams.indexOf(team)

	// Simulate the cache initialization that happens in graphQlApi.login()
	mockState.currentUser = deepClone(user)
	mockState.jwt = mintMockJwt(user, team)

	console.log("Mock login successful for <" + user.email + "> into team '" + team.teamName + "'")

	return {
		// Enriched, not a bare deepClone: userAlreadyVoted/numBallots are PER-USER computed values
		// (see enrichPollForCurrentUser), and mockState.currentUser was just set above to whoever is
		// logging in now - a stale/bare clone would carry another user's already-voted state instead.
		team: enrichTeamForCurrentUser(team),
		user: deepClone(user),
		jwt: mockState.jwt,
		// ALL teams of this user, so the frontend can offer the team switcher.
		teams: usersTeams.map(t => ({ id: t.id, teamName: t.teamName })),
	}
}

const countVotesForPoll = pollId => Object.keys(mockState.ballotsByPollAndUser)
	.filter(key => key.startsWith(`${pollId}:`)).length

const currentUserId = () => mockState.currentUser?.id

const hasCurrentUserVoted = pollId => {
	const userId = currentUserId()
	if (userId == null) return false
	return Boolean(get(mockState, `ballotsByPollAndUser.${ballotKey(pollId, userId)}`))
}

const enrichPollForCurrentUser = poll => ({
	...deepClone(poll),
	userAlreadyVoted: hasCurrentUserVoted(poll.id),
	// The backend derives numBallots live from the ballots table (PollEntity.getNumBallots()).
	// Mirror that here: the seeded baseline plus every ballot cast during this mock session.
	numBallots: (poll.numBallots || 0) + countVotesForPoll(poll.id),
})

const enrichTeamForCurrentUser = team => ({
	...deepClone(team),
	polls: (team.polls || []).map(enrichPollForCurrentUser),
})




	
/**
 * Like the backend's CastVoteService.assertVotingIsOpen: from votingEndAt on, nothing is accepted.
 * @param {Number} notStartedCode the error for a poll that is not in VOTING (yet)
 */
const assertVotingIsOpen = (poll, notStartedCode) => {
	if (poll.status === "FINISHED") rejectLiquido(LiquidoExceptionCodes.VOTING_ENDED, "Voting has ended")
	if (poll.status !== "VOTING") rejectLiquido(notStartedCode, "Poll is not in its voting phase")
	const end = Date.parse(poll.votingEndAt)
	if (!isNaN(end) && Date.now() >= end) rejectLiquido(LiquidoExceptionCodes.VOTING_ENDED, "Voting has ended")
}

/**
 * Finish a poll: pick a winner, set the end and the proposal statuses. Returns the winner. Without a single
 * ballot there is no winner - like the backend (PollService.calcWinnerOfPoll).
 */
const closePoll = (poll, votingEndAt) => {
	const hasBallots = (poll.numBallots || 0) + countVotesForPoll(poll.id) > 0
	const winner = !hasBallots ? null : (poll.proposals || [])
		.slice()
		.sort((a, b) => (b.numSupporters || 0) - (a.numSupporters || 0))[0] || null
	poll.status = "FINISHED"
	poll.votingEndAt = votingEndAt
	poll.updatedAt = nowIso()
	poll.winner = winner ? deepClone(winner) : null
	;(poll.proposals || []).forEach(proposal => {
		proposal.status = winner && proposal.id === winner.id ? "LAW" : "LOST"   // the backend's ProposalEntity.LawStatus
	})
	return winner
}

/**
 * The backend's PollCloser, without a scheduler: before every request, finish each running poll
 * whose end has passed, keeping its planned end as the official one.
 */
const closeOverduePolls = () => {
	const now = Date.now()
	for (const team of mockState.teams || []) {
		for (const poll of team.polls || []) {
			const end = Date.parse(poll.votingEndAt)
			if (poll.status === "VOTING" && !isNaN(end) && end <= now) closePoll(poll, poll.votingEndAt)
		}
	}
}

const queryHandlers = {
	ping: () => "MOCK responses are active!",
	// Read straight off the live config module, so the mock can never drift from config.common.js.
	liquidoConfig: () => ({
		usernameMinLength: config.usernameMinLength,
		inviteCodeLength: config.inviteCodeLength,
		minPasswordLength: config.minPasswordLength,
		pollTitleMinLength: config.pollTitleMinLength,
		pollDefaultRuntimeDays: config.pollDefaultRuntimeDays,
		minVotingMinutes: config.minVotingMinutes,
		maxVotingDays: config.maxVotingDays,
		votingEndEarliestHour: config.votingEndEarliestHour,
		proposalTitleMinLength: config.proposalTitleMinLength,
		proposalDescriptionMinLength: config.proposalDescriptionMinLength,
		inviteLinkPrefix: config.inviteLinkPrefix,
		pollyNameMaxLength: config.pollyNameMaxLength,
		pictureMaxBytes: config.pictureMaxBytes,
		pictureSize: config.pictureSize,
	}),
	team: () => enrichTeamForCurrentUser(currentTeam()),
	loginWithJwt: () => {
		console.log("========> MOCKED loginWithJwt")
		const member = findMemberByJwt(jwtFromAuthHeader())
		if (!member) {
			rejectLiquido(LiquidoExceptionCodes.JWT_TOKEN_INVALID, "Invalid mock JWT")
		}
		return loginMock(member.user.email)
	},
	loginWithEmailPassword: (query, variables = {}) => {
		const email = get(variables, "email", argFromQuery(query, "email"))
		if (!findMemberByEmail(email)) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_EMAIL_NOT_FOUND, "Unknown email")
		}
		return loginMock(email)
	},
	googleOneTapLogin: () => {
		const user = currentUserOrThrow()
		return loginMock(user.email)
	},
	authToken: (query, variables = {}) => {
		const mobilephone = get(variables, "mobilephone", argFromQuery(query, "mobilephone"))
		const member = findMemberByMobile(mobilephone)
		if (!member) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_MOBILE_NOT_FOUND, "Unknown mobilephone")
		}
		set(mockState, `issuedAuthTokensByMobile.${mobilephone}`, config.devLogin.mockSmsToken)
		return true
	},
	loginWithAuthToken: (query, variables = {}) => {
		const mobilephone = get(variables, "mobilephone", argFromQuery(query, "mobilephone"))
		const authToken = get(variables, "authToken", argFromQuery(query, "authToken"))
		const member = findMemberByMobile(mobilephone)
		if (!member) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_MOBILE_NOT_FOUND, "Unknown mobilephone")
		}
		if (get(mockState, `issuedAuthTokensByMobile.${mobilephone}`) !== authToken) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_TOKEN_INVALID, "Invalid auth token")
		}
		return loginMock(member.user.email)
	},
	devLogin: (query, variables = {}) => {
		const email = get(variables, "email", argFromQuery(query, "email"))
		if (!findMemberByEmail(email)) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_EMAIL_NOT_FOUND, "Unknown user for devLogin")
		}
		// Not sent by the real client today (devLogin only takes email + token), but accepted here
		// defensively in case that ever changes - harmless when absent, since loginMock(email,
		// undefined) just falls back to its normal default-team resolution.
		const teamId = get(variables, "teamId", argFromQuery(query, "teamId"))
		return loginMock(email, teamId != null && teamId !== "null" ? asInt(teamId) : undefined)
	},
	polls: () => (currentTeam().polls || []).map(enrichPollForCurrentUser),
	poll: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		return enrichPollForCurrentUser(poll)
	},
	publishedTally: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		const proposalOrder = (poll.proposals || []).map(p => p.id).sort((a, b) => a - b)
		const winnerIndex = poll.winner ? proposalOrder.indexOf(poll.winner.id) : -1
		// Fake but internally consistent head-to-head numbers: the winner beats every other proposal,
		// and among the remaining proposals earlier ids beat later ones. Good enough to render a
		// plausible-looking duel matrix/graph in mock-backend mode; not meant to be mathematically real.
		const n = proposalOrder.length
		const duelMatrix = Array.from({ length: n }, () => Array.from({ length: n }, () => 0))
		for (let i = 0; i < n; i++) {
			for (let j = 0; j < n; j++) {
				if (i === j) continue
				if (i === winnerIndex) duelMatrix[i][j] = 5
				else if (j === winnerIndex) duelMatrix[i][j] = 3
				else if (i < j) duelMatrix[i][j] = 4
				else duelMatrix[i][j] = 2
			}
		}
		return {
			pollId,
			pollTitle: poll.title,
			proposalOrder,
			duelMatrix,
			winnerId: poll.winner ? poll.winner.id : null,
			winnerIds: poll.winner ? [poll.winner.id] : [],
			// Same seeded-baseline-plus-session-ballots computation as enrichPollForCurrentUser: a poll
			// created during this session (not from the seed) starts with no numBallots field at all,
			// so poll.numBallots alone is always 0 and the winner page's "more details" never showed.
			numBallots: (poll.numBallots || 0) + countVotesForPoll(pollId),
		}
	},
	voterToken: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const user = currentUserOrThrow()
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		assertVotingIsOpen(poll, LiquidoExceptionCodes.CANNOT_CREATE_VOTING_TOKEN)
		const token = `mock-voter-${pollId}-${user.id}`
		set(mockState, `voterTokensByPollAndUser.${ballotKey(pollId, user.id)}`, token)
		return token
	},
	myBallot: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const user = currentUserOrThrow()
		return deepClone(get(mockState, `ballotsByPollAndUser.${ballotKey(pollId, user.id)}`, null))
	},
	verifyBallot: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const checksum = get(variables, "checksum", argFromQuery(query, "checksum"))
		const match = Object.entries(mockState.ballotsByPollAndUser)
			.find(([key, ballot]) => key.startsWith(`${pollId}:`) && ballot?.checksum === checksum)
		return match ? deepClone(match[1]) : null
	},
	teamForInviteCode: (query, variables = {}) => {
		const inviteCode = get(variables, "inviteCode", argFromQuery(query, "inviteCode"))
		const team = mockState.teams.find(t => t.inviteCode === inviteCode)
		if (!team) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_JOIN_TEAM_INVITE_CODE_INVALID, "Invite code not found")
		}
		return deepClone(team)
	},
	/**
	 * Mocks the backend's getNewestSeedTeam: the newest TestDataCreator seed team, found by its name
	 * PREFIX "Test Team " (the rest is a timestamp), like the backend's TeamEntity.findNewestByPrefix(SEED_TEAM_PREFIX). With
	 * teamsSeed.json loaded that is the exported seed team; without one, the current team.
	 */
	newestSeedTeam: (query, variables = {}) => {
		const devLoginToken = get(variables, "devLoginToken", argFromQuery(query, "devLoginToken"))
		if (devLoginToken !== config.devLogin.token) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_LOGIN_TOKEN_INVALID, "Invalid devLoginToken passed.")
		}
		const seedTeams = mockState.teams.filter(t => /^Test Team \d+$/.test(t.teamName || ""))
		const newest = seedTeams.sort((a, b) => b.teamName.localeCompare(a.teamName))[0]
		return deepClone(newest || currentTeam())
	},
}

const mutationHandlers = {
	/**
	 * Switch the session into another team of the current user. loginMock() does the membership
	 * check and rejects a team the user does not belong to, exactly as the real backend does.
	 */
	switchTeam: (query, variables = {}) => {
		const user = currentUserOrThrow()
		const teamId = asInt(get(variables, "teamId", argFromQuery(query, "teamId", "-1")))
		return loginMock(user.email, teamId)
	},
	createNewTeam: (query, variables = {}) => {
		const teamName = get(variables, "teamName", argFromQuery(query, "teamName", "Mock Team"))
		const admin = get(variables, "admin", { name: "Mock Admin", email: `admin.${Date.now()}@mock.local` })
		const userId = Date.now()
		const adminUser = {
			id: userId,
			name: admin.name || "Mock Admin",
			email: cleanEmail(admin.email) || `admin.${userId}@mock.local`,
			mobilephone: admin.mobilephone || null,
			picture: admin.picture || "Avatar1.png",
			website: admin.website || null,
			// A freshly registered user has not clicked the link in their welcome mail yet - matches
			// the real backend, and is what shows team-home.vue's "please verify your email" reminder.
			emailVerified: false,
		}
		const newTeam = {
			id: userId,
			teamName,
			inviteCode: Math.random().toString(36).slice(2, 10),
			members: [{ role: "ADMIN", joinedAt: nowIso(), user: adminUser }],
			polls: [],
		}
		// ADD the team, like the real backend does - don't replace the team list. Other specs in the
		// same run still need the existing teams (the fixed test teams from teamsSeed.json above
		// all), and the id counters must keep counting, or new polls would collide with seeded ids.
		mockState.teams.push(newTeam)
		mockState.currentTeamIndex = mockState.teams.length - 1
		mockState.currentUser = adminUser
		mockState.jwt = mintMockJwt(adminUser, newTeam)

		// Return login result with the newly created team and admin
		return {
			team: deepClone(newTeam),
			user: deepClone(adminUser),
			jwt: mockState.jwt,
			teams: [{ id: newTeam.id, teamName: newTeam.teamName }],
		}
	},
	joinTeam: (query, variables = {}) => {
		const inviteCode = get(variables, "inviteCode", argFromQuery(query, "inviteCode"))
		// Find the team by its invite code across ALL teams, like the backend - not just the current one.
		const team = mockState.teams.find(t => t.inviteCode === inviteCode)
		if (!team) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_JOIN_TEAM_INVITE_CODE_INVALID, "Invite code invalid")
		}
		mockState.currentTeamIndex = mockState.teams.indexOf(team)
		const memberInput = get(variables, "member", null)
		if (!memberInput || !isValidString(memberInput.email)) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_REGISTER_NEED_EMAIL, "Email is required")
		}
		const existing = findMemberByEmail(memberInput.email)
		if (existing) {
			// User already exists, just log them in
			return loginMock(existing.user.email)
		}
		// Create new team member
		const newUser = {
			id: Date.now(),
			name: memberInput.name || memberInput.email,
			email: cleanEmail(memberInput.email),
			mobilephone: memberInput.mobilephone || null,
			picture: memberInput.picture || "Avatar1.png",
			website: memberInput.website || null,
			emailVerified: false,   // see the identical comment in createNewTeam above
		}
		currentTeam().members.push({ role: "MEMBER", joinedAt: nowIso(), user: newUser })
		// Log in the newly created member
		return loginMock(newUser.email)
	},
	createPoll: (query, variables = {}) => {
		const title = get(variables, "title", argFromQuery(query, "title", "New Mock Poll"))
		const membersCanAddProposals = get(variables, "membersCanAddProposals", argFromQuery(query, "membersCanAddProposals", false))
		const poll = {
			id: mockState.nextPollId++,
			title,
			status: "ELABORATION",
			createdAt: nowIso(),
			updatedAt: nowIso(),
			votingStartAt: null,
			votingEndAt: null,
			userAlreadyVoted: false,
			membersCanAddProposals,
			proposals: [],
			winner: null,
		}
		currentTeam().polls.unshift(poll)
		return enrichPollForCurrentUser(poll)
	},
	updatePoll: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const title = get(variables, "title", argFromQuery(query, "title"))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		if (poll.status !== "ELABORATION") {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_UPDATE_POLL, `Cannot rename poll: poll(id=${pollId}) has already started.`)
		}
		poll.title = title
		poll.updatedAt = nowIso()
		return deepClone(poll)
	},
	addProposal: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		const user = currentUserOrThrow()
		poll.proposals.push({
			id: mockState.nextProposalId++,
			title: get(variables, "title", argFromQuery(query, "title", "Mock proposal")),
			description: get(variables, "description", argFromQuery(query, "description", "")),
			icon: get(variables, "icon", argFromQuery(query, "icon", "vote-yea")),
			status: poll.status === "VOTING" ? "VOTING" : "ELABORATION",
			createdAt: nowIso(),
			numSupporters: 0,
			likedByCurrentUser: false,
			createdBy: deepClone(user),
		})
		poll.updatedAt = nowIso()
		return deepClone(poll)
	},
	updateProposal: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const proposalId = asInt(get(variables, "proposalId", argFromQuery(query, "proposalId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		const proposal = (poll.proposals || []).find(p => p.id === proposalId)
		if (!proposal) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Proposal ${proposalId} not found`)
		proposal.title = get(variables, "title", argFromQuery(query, "title", proposal.title))
		proposal.description = get(variables, "description", argFromQuery(query, "description", proposal.description))
		proposal.icon = get(variables, "icon", argFromQuery(query, "icon", proposal.icon))
		poll.updatedAt = nowIso()
		return deepClone(poll)
	},
	deleteProposal: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const proposalId = asInt(get(variables, "proposalId", argFromQuery(query, "proposalId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		poll.proposals = (poll.proposals || []).filter(p => p.id !== proposalId)
		poll.updatedAt = nowIso()
		return deepClone(poll)
	},
	likeProposal: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const proposalId = asInt(get(variables, "proposalId", argFromQuery(query, "proposalId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		const proposal = (poll.proposals || []).find(p => p.id === proposalId)
		if (!proposal) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Proposal ${proposalId} not found`)
		proposal.likedByCurrentUser = !proposal.likedByCurrentUser
		proposal.numSupporters = Math.max(0, (proposal.numSupporters || 0) + (proposal.likedByCurrentUser ? 1 : -1))
		poll.updatedAt = nowIso()
		return deepClone(poll)
	},
	/** Same limits as the backend's PollService.startVotingPhase: the end lies minVotingMinutes to maxVotingDays ahead. */
	startVotingPhase: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const votingEndAt = get(variables, "votingEndAt", argFromQuery(query, "votingEndAt"))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		if (poll.status !== "ELABORATION" || (poll.proposals || []).length < 2) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_START_VOTING_PHASE, "Need a poll in ELABORATION with at least two proposals")
		}
		const now = Math.floor(Date.now() / 1000) * 1000
		const end = Math.floor(Date.parse(votingEndAt) / 1000) * 1000   // the backend stores whole seconds
		if (isNaN(end) || end < now + config.minVotingMinutes * 60 * 1000 || end > now + config.maxVotingDays * 24 * 60 * 60 * 1000) {
			rejectLiquido(LiquidoExceptionCodes.INVALID_VOTING_END, `Invalid votingEndAt ${votingEndAt}`)
		}
		poll.status = "VOTING"
		poll.votingStartAt = new Date(now).toISOString()
		poll.votingEndAt = new Date(end).toISOString()
		poll.updatedAt = nowIso()
		;(poll.proposals || []).forEach(p => {
			if (p.status === "ELABORATION") p.status = "VOTING"
		})
		return deepClone(poll)
	},
	finishVotingPhase: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		// Like the backend: finished early, the end becomes now; after the planned end, that end stays.
		const planned = Date.parse(poll.votingEndAt)
		const end = !isNaN(planned) && planned <= Date.now() ? poll.votingEndAt : nowIso()
		return deepClone(closePoll(poll, end))
	},
	castVote: (query, variables = {}) => {
		const pollId = asInt(get(variables, "pollId", argFromQuery(query, "pollId", "-1")))
		const voteOrderIds = variables?.voteOrderIds || voteOrderFromQuery(query)
		const voterToken = get(variables, "voterToken", argFromQuery(query, "voterToken"))
		const user = currentUserOrThrow()
		const poll = findPoll(pollId)
		if (!poll) rejectLiquido(LiquidoExceptionCodes.CANNOT_FIND_ENTITY, `Poll ${pollId} not found`)
		assertVotingIsOpen(poll, LiquidoExceptionCodes.CANNOT_CAST_VOTE)
		const expectedToken = get(mockState, `voterTokensByPollAndUser.${ballotKey(pollId, user.id)}`)
		if (!expectedToken || expectedToken !== voterToken) {
			rejectLiquido(LiquidoExceptionCodes.INVALID_VOTER_TOKEN, "Invalid voter token")
		}
		const proposalIds = new Set((poll.proposals || []).map(p => p.id))
		if (voteOrderIds.length === 0 || voteOrderIds.some(id => !proposalIds.has(id))) {
			rejectLiquido(LiquidoExceptionCodes.CANNOT_CAST_VOTE, "Invalid vote order")
		}
		const ballot = {
			level: 0,
			checksum: `mock-${pollId}-${user.id}-${Date.now()}`,
			voteOrder: voteOrderIds.map(id => ({ id })),
		}
		set(mockState, `ballotsByPollAndUser.${ballotKey(pollId, user.id)}`, ballot)
		// NOT poll.userAlreadyVoted = true: that field is PER-USER (see enrichPollForCurrentUser /
		// hasCurrentUserVoted), and this poll object is shared by every voter - setting it here would
		// make the poll look already-voted to everyone the moment anyone casts a ballot.
		poll.updatedAt = nowIso()
		return {
			// How often THIS ballot counts - 1 plus the votes delegated to this voter as a proxy (see
			// cast-vote.vue's voteCountedNTimes). Not the poll's total: that is numBallots. The mock has
			// no delegations, so it is always 1.
			voteCount: 1,
			ballot: deepClone(ballot),
		}
	},

	/**
	 * Like the backend's UserPictureService: the picture is stored under a new random key on every upload, and
	 * user.picture points at it. A user in several teams has one user object per team - all of them change.
	 */
	uploadMyPicture: (query, variables = {}) => {
		const user = currentUserOrThrow()
		const base64 = String(get(variables, "base64", "")).replace(/^data:[^,]*,/, "").replace(/\s/g, "")
		if (!/^[A-Za-z0-9+/]+=*$/.test(base64)) rejectLiquido(LiquidoExceptionCodes.CANNOT_UPLOAD_PICTURE, "The picture is not an image.")
		const byteCount = Math.floor(base64.length * 3 / 4) - (base64.match(/=*$/)[0].length)
		if (byteCount > config.pictureMaxBytes) rejectLiquido(LiquidoExceptionCodes.CANNOT_UPLOAD_PICTURE, "The picture is too large.")
		removePictureOf(user)
		const picKey = Math.random().toString(36).substring(2) + Date.now().toString(36)
		mockState.picturesByKey[picKey] = base64
		return setPictureOf(user, PICTURE_PATH + picKey)
	},

	removeMyPicture: () => {
		const user = currentUserOrThrow()
		removePictureOf(user)
		return setPictureOf(user, null)
	},
}

/** Where the backend serves a profile picture - UserPictureService.PICTURE_PATH. */
export const PICTURE_PATH = "/picture/"

const removePictureOf = user => {
	if (user.picture?.startsWith(PICTURE_PATH)) delete mockState.picturesByKey[user.picture.substring(PICTURE_PATH.length)]
}

const setPictureOf = (user, picture) => {
	mockState.teams.flatMap(t => t.members || [])
		.filter(m => cleanEmail(m.user?.email) === cleanEmail(user.email))
		.forEach(m => { m.user.picture = picture })
	mockState.currentUser.picture = picture
	const { id, name, email, mobilephone, website } = mockState.currentUser
	return { id, name, email, mobilephone, picture, website }
}

/**
 * Runs one GraphQL mock operation and returns its `data` payload. Throws MockLiquidoError for a
 * rejected operation, or a plain Error for a query this mock does not recognize at all. Exported so
 * liquido-mock-http-server.js's writeGraphQlResponse() can share this same dispatch.
 */
export const runMockOperation = (query, variables) => {
	console.log("MOCK request for", query, variables)
	const operation = detectOperation(query)
	if (!operation) throw new Error(`Unhandled mock query: ${query}`)
	const isMutation = /^\s*mutation\b/.test(query)
	const handler = isMutation ? mutationHandlers[operation] : queryHandlers[operation]
	if (!handler) throw new Error(`Unhandled mock ${isMutation ? "mutation" : "query"}: ${operation}`)
	closeOverduePolls()
	const payload = handler(query, variables)
	return operation === "myBallot" ? { myBallot: payload, ballot: payload } : { [operation]: payload }
}

/**
 * Promise-based mock GraphQL call, matching graphQlQuery()'s real-backend contract exactly:
 * resolves {data}, or rejects with the GraphQL response body plus `liquidoException`, which is what
 * graphQlQuery() rejects with. Deliberately no axios-style `response` - a GraphQL error never has one, and
 * faking it once let callers read REST and GraphQL errors from places the real backend never fills.
 *
 * This is a SEPARATE path from the real-HTTP one in liquido-mock-http-server.js: vitest unit tests
 * run in plain Node/jsdom with no Vite dev server to route an HTTP call through at all, so
 * liquido-graphql-client.js's graphQlQuery() calls this directly for config.test.js (configSource
 * "test"). Cypress e2e runs use config.development.js instead, which goes through the real HTTP
 * path so that cy.intercept()/cy.wait() can see mocked traffic exactly like they would the real
 * backend - see this file's module doc comment at the top.
 */
export const graphQlQueryMock = (query, variables) => {
	try {
		return Promise.resolve({ data: runMockOperation(query, variables) })
	} catch (err) {
		if (err instanceof MockLiquidoError) {
			const liquidoException = mockLiquidoException(err.code, err.message)
			return Promise.reject({
				liquidoException,
				errors: [{ message: err.message, extensions: { liquidoException } }],
			})
		}
		return Promise.reject(err)
	}
}

/**
 * Back to a fresh database: over HTTP by POST /mock/reset, which the red LIQUIDO icon in
 * liquido-header.vue calls, and directly by any vitest spec that needs a clean slate.
 */
export const resetGraphQlMockState = () => { mockState = createState() }
